@import "../../common/base";

:host {
  /**
   * @prop --ch-tree-view-item-custom-padding-inline-start:
   * Specifies an additional value for the padding-inline-start of the items
   * without breaking the indentation of the items.
   * @default 0px
   */
  --ch-tree-view-item-custom-padding-inline-start: 0px;

  /**
   * @prop --ch-tree-view-item-gap:
   * Specifies the spacing between the images, checkbox, text and the
   * expandable button of the items.
   * @default 0px
   */
  --ch-tree-view-item-gap: 0px;

  /**
   * @prop --ch-tree-view-item__checkbox-size:
   * Specifies the checkbox size of the items.
   * @default #{$default-decorative-image-size}
   */
  --ch-tree-view-item__checkbox-size: #{$default-decorative-image-size};

  /**
   * @prop --ch-tree-view-item__checkbox-size:
   * Specifies the expandable button size of the items.
   * @default #{$default-decorative-image-size}
   */
  --ch-tree-view-item__expandable-button-size: #{$default-decorative-image-size};

  /**
   * @prop --ch-tree-view-item__image-size:
   * Specifies the box size that contains the start or end images of the items.
   * @default #{$default-decorative-image-size}
   */
  --ch-tree-view-item__image-size: #{$default-decorative-image-size};

  /**
   * @prop --ch-tree-view-item__image-size:
   * Specifies the size of the start and end images of the items.
   * @default 100%
   */
  --ch-tree-view-item__background-image-size: 100%;

  /**
   * @prop --ch-tree-view-item__line--inset-inline-start:
   * Specifies an additional separation that only applies for the inline start
   * position of the item's line.
   * @default 0px
   */
  --ch-tree-view-item__line--inset-inline-start: 0px;

  /**
   * @prop --ch-tree-view-item__line--inset-inline-end:
   * Specifies the separation that only applies for the inline end position of
   * the item's line. This separation visually shows a gap between the line and
   * the item's header.
   * @default 0px
   */
  --ch-tree-view-item__line--inset-inline-end: 0px;

  --ch-tree-tab-size: calc(
    var(--ch-tree-view-item__image-size) +
      var(--ch-tree-view-item__expandable-button-size) +
      (var(--ch-tree-view-item-gap) * 2)
  );

  display: grid;
  grid-auto-rows: max-content;
  block-size: 100%;
  overflow: auto;
  contain: size;
}

// Reset useful droppable areas
.dragging-item ch-tree-view-item {
  pointer-events: var(--ch-tree-view-pointer-events, all);
}

// Remove un-useful droppable areas
.dragging-item .item-deny-drop {
  pointer-events: none;
}

// Remove droppable areas on selected items
.dragging-selected-items ch-tree-view-item[selected] {
  --ch-tree-view-pointer-events: none;
}

// - - - - - - - - - - - - - - - -
//        Item indentation
// - - - - - - - - - - - - - - - -
::part(item__header),
ch-tree-view-drop {
  --own-offset: 0px;

  --ch-tree-view-item-indentation: calc(
    var(--ch-tree-tab-size) * var(--level) - var(--own-offset) -
      var(--parent-offset, 0px) +
      var(--ch-tree-view-item-custom-padding-inline-start)
  );
}

// Properly ident first level leafs when the expand button is visible
::part(item__header level-0-leaf) {
  --ch-tree-view-item-indentation: calc(
    var(--ch-tree-view-item__expandable-button-size) +
      var(--ch-tree-view-item-gap) +
      var(--ch-tree-view-item-custom-padding-inline-start)
  );
}

::part(item__header expand-button),
ch-tree-view-drop {
  --own-offset: calc(
    var(--ch-tree-view-item__expandable-button-size) +
      var(--ch-tree-view-item-gap)
  );
}

// Even level
::part(item__header even-level),
ch-tree-view-drop.gx-even-level {
  --parent-offset: var(--parent-offset-odd);
}

// Odd level
::part(item__header odd-level),
ch-tree-view-drop.gx-odd-level {
  --parent-offset: var(--parent-offset-even);
}

ch-tree-view-drop {
  margin-inline-start: var(--ch-tree-view-item-indentation);
}

::part(item__header) {
  padding-inline-start: var(--ch-tree-view-item-indentation);
}

::part(item__group even-level) {
  --parent-offset-even: calc(
    var(--ch-tree-view-item__expandable-button-size) +
      var(--ch-tree-view-item-gap) + var(--parent-offset-odd, 0px)
  );
}

::part(item__group odd-level) {
  --parent-offset-odd: calc(
    var(--ch-tree-view-item__expandable-button-size) +
      var(--ch-tree-view-item-gap) + var(--parent-offset-even, 0px)
  );
}

ch-tree-view-item {
  // Improve the position of the first-child to avoid clipping the droppable
  // area, since the parent has content-visibility: "auto" which creates a new
  // containing block
  & > ch-tree-view-drop:first-child {
    transform: translateY(0);

    &::after {
      inset-block-start: 0px;
    }
  }

  // Improve collision with the end drop (level N + 1) of a group and the next
  // drop in the level N
  & > ch-tree-view-drop:last-child {
    transform: translateY(calc(var(--ch-tree-view-drop--size) * -1));

    &::after {
      inset-block-start: calc(var(--ch-tree-view-drop--size) * 0.5);
    }
  }
}
